import {
    Checkbox,
    createStyles,
    Fab,
    FormControl,
    FormControlLabel,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    WithStyles,
    withStyles,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { inject } from 'mobx-react';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import FreeTypeAheadField from 'src/common/FreeTypeAheadField/FreeTypeAheadField';
import {
    DefaultActionItem,
    IActionItem,
    IActionStatus,
    IAssessment,
    IBranch,
} from 'src/Definitions';
import { IStores } from 'src/Stores';
import ActionStatuses from 'src/utils/ActionStatuses';
import ActionItem from './ActionItem';
import ActionItemDialog from './ActionItemDialog';

const styles = createStyles({
    actionItemBoard: {
        height: '100%',
        overflow: 'auto',
        padding: '20px 40px',
    },
    boardColumn: {
        height: '100%',
        overflowY: 'auto',
        padding: '10px',
        width: '15vw',
    },
    checkBoxLabel: {
        paddingLeft: '20px',
    },
    columnContainerDiv: {
        backgroundColor: '#F4F5F7',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    columnHeader: {
        color: '#808B9D',
        fontSize: '18pt',
        marginBottom: '20px',
        textAlign: 'center',
    },
    formControl: {
        display: 'flex',
        wrap: 'nowrap',
    },
    searchInput: {
        marginBottom: '15px',
        marginLeft: '20px',
    },
});

interface IActionItemsProps extends WithStyles<typeof styles> {
    actionItems?: IActionItem[];
    actionStatuses?: IActionStatus[];
    assessments?: IAssessment[];
    assignees?: string[];
    allRegionNames?: string[];
    branches?: IBranch[];
    selectedBranches?: IBranch[];
    getAllAssessments?: (
        filter?: string,
        pageNumber?: number,
        pageSize?: number,
        showCompleted?: boolean,
        showVisitEnded?: boolean
    ) => void;
    getAllBranches?: (pageNumber?: number, pageSize?: number, filter?: string) => void;
    filter?: string;
    filterByDueIn30?: boolean;
    filterByOverdue?: boolean;
    getActionStatuses?: () => void;
    getAllActionItems?: (shouldGetRegionNames?: boolean, shouldGetAssignees?: boolean) => Promise<void>;
    isModalOpen?: boolean;
    onSearchChanged?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    openDialogWithActionItem?: (actionItem: IActionItem) => void;
    setSelectedRegions?: (regions: string[]) => void;
    setSelectedBranches?: (branches: IBranch[]) => void;
    saveActionItem?: (actionItem: IActionItem) => Promise<void>;
    setSelectedAssessments?: (assessments: IAssessment[]) => void;
    setSelectedAssignees?: (assignees: string[]) => void;
    setFilteredAssignees?: (filter: string) => void;
    selectedAssessments?: IAssessment[];
    selectedAssignees?: string[];
    selectedRegions?: string[];
    showArchived?: boolean;
    updateFilters?: (
        filterByDueIn30: boolean,
        filterByOverdue: boolean,
        filterByCompleted: boolean,
        showArchived: boolean
    ) => void;
}

class ActionItems extends React.Component<IActionItemsProps> {

    public componentDidMount() {
        this.getAllActionItems(true, true);
        this.props.getActionStatuses!();
    }

    public getAllActionItems(shouldLoadRegions: boolean, shouldLoadAssignees: boolean) {
        this.props.getAllActionItems!(shouldLoadRegions, shouldLoadAssignees);
    }

    public filterRenderValue = (items: string[]) => (selectedItems: string[]) => {
        return items
            .filter((selected) => selectedItems.includes(selected))
            .map((name, index) => `${index ? ', ' : ''} ${name}`);
    };

    public render() {
        const {
            actionItems,
            assessments,
            assignees,
            allRegionNames,
            branches,
            classes,
            filter,
            getAllAssessments,
            getAllBranches,
            onSearchChanged,
            selectedAssessments,
            selectedAssignees,
            selectedBranches,
            selectedRegions,
            setFilteredAssignees,
            setSelectedAssessments,
            setSelectedAssignees,
            setSelectedBranches,
            setSelectedRegions,
            filterByDueIn30,
            filterByOverdue,
            showArchived
        } = this.props;
        const actionItemDialog = this.props.isModalOpen && <ActionItemDialog />;
        const actionStatuses = this.props.actionStatuses;

        const filterByOverdueCheckbox = (
            <FormControlLabel
                className={classes.checkBoxLabel}
                control={
                    <Checkbox
                        data-cy="filterByOverdue"
                        checked={filterByOverdue}
                        color="primary"
                        name="filterByOverdue"
                        onChange={this.onFilterByOverdueChange}
                        value="shouldFilterByOverdue"
                    />
                }
                label="Filter by Overdue"
            />
        );

        const filterByDueIn30Checkbox = (
            <FormControlLabel
                className={classes.checkBoxLabel}
                control={
                    <Checkbox
                        data-cy="filterByDueIn30"
                        checked={filterByDueIn30}
                        color="primary"
                        name="filterByDueIn30"
                        onChange={this.onFilterByDueIn30Change}
                        value="shouldFilterByDueIn30"
                    />
                }
                label="Filter by Due in 30 days"
            />
        );

        const showArchivedCheckbox = (
            <FormControlLabel
                className={classes.checkBoxLabel}
                control={
                    <Checkbox
                        data-cy="showArchived"
                        checked={showArchived}
                        color="primary"
                        name="showArchived"
                        onChange={this.onShowArchived}
                        value="shouldShowArchived"
                    />
                }
                label="Show Archived"
            />
        );

        function handleRegionSelectChange(event: React.ChangeEvent<{ value: any }>) {
            const newSelectedRegions = event.target.value as string[];
            setSelectedRegions!(newSelectedRegions);
        }

        const filterByRegion = (
            <FormControl className={classes.formControl}>
                <InputLabel>Regions</InputLabel>
                <Select
                    data-cy="regionsFilter"
                    multiple
                    value={selectedRegions || []}
                    onChange={handleRegionSelectChange}
                    input={<Input id="select-multiple" />}
                    renderValue={this.filterRenderValue(allRegionNames || [])}
                >
                    {(allRegionNames || []).map((name: string, index: number) => (
                        <MenuItem key={index} value={name}>
                            <Checkbox checked={(selectedRegions as any).includes(name)} />
                            {name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );

        const filterByAssessment = (
            <FormControl className={classes.formControl}>
                <FreeTypeAheadField
                    data-cy="assessmentsFilter"
                    label="Assessments"
                    getName={getAssessmentName}
                    getValue={getAssessmentValue}
                    fullWidth
                    getItems={handleUpdateAssessmentSearch}
                    items={assessments || []}
                    onChipChange={handleAssessmentSelectChange}
                    selectedItems={selectedAssessments || []}
                />
            </FormControl>
        );

        function getAssessmentValue(assessment: IAssessment) {
            return assessment ? assessment.id : '';
        }

        function getAssessmentName(assessment: IAssessment) {
            return assessment ? assessment.name : '';
        }

        function handleUpdateAssessmentSearch(newFilter: string) {
            getAllAssessments!(newFilter, undefined, 5000, true, true);
        }

        function handleAssessmentSelectChange(newAssessments: IAssessment[]) {
            setSelectedAssessments!(newAssessments);
        }

        function handleUpdateBranchSearch(newFilter: string) {
            getAllBranches!(undefined, 5000, newFilter);
        }

        function getBranchValue(branch: IBranch) {
            return branch ? branch.id : '';
        }

        function getBranchName(branch: IBranch) {
            return branch ? branch.name : '';
        }

        function handleChipChange(chipBranches: IBranch[]) {
            setSelectedBranches!(chipBranches);
        }

        const filterByAssignee = (
            <FormControl className={classes.formControl}>
                <FreeTypeAheadField
                    label="Assignees"
                    fullWidth={true}
                    getName={getAssigneeName}
                    getValue={getAssigneeValue}
                    getItems={getAssignees}
                    items={assignees || []}
                    onChipChange={handleAssigneeChange}
                    selectedItems={selectedAssignees || []}
                />
            </FormControl>
        );

        function getAssignees(newFilter: string) {
            if (actionItems && newFilter) {
                setFilteredAssignees!(newFilter);
            }
        }

        function getAssigneeName(assignee: string) {
            return assignee;
        }

        function getAssigneeValue(assignee: string) {
            return assignee;
        }

        function handleAssigneeChange(newAssignees: string[]) {
            setSelectedAssignees!(newAssignees);
        }

        return (
            <Grid
                container
                direction="column"
                justify="space-around"
                wrap="nowrap"
                className={this.props.classes.actionItemBoard}
            >
                <Grid container direction="row" justify="space-around">
                    <Grid item xs={4}>
                        <Grid container direction="column">
                            <Grid item>
                                <h1>Action Items</h1>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Create Action Item">
                                    <Fab
                                        color="primary"
                                        data-cy="action-button"
                                        onClick={this.openDialog(DefaultActionItem(true))}
                                    >
                                        <Add />
                                    </Fab>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={8}>
                        <Grid container direction="column">
                            <Grid container direction="row" spacing={8}>
                                <Grid item xs={3}>
                                    {filterByRegion}
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        label="Search"
                                        margin="none"
                                        name="filter"
                                        onChange={onSearchChanged}
                                        value={filter}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    {filterByAssessment}
                                </Grid>
                                <Grid item xs={3}>
                                    <FormControl className={classes.searchInput}>
                                        <FreeTypeAheadField
                                            label="Branches"
                                            fullWidth={true}
                                            getName={getBranchName}
                                            getValue={getBranchValue}
                                            getItems={handleUpdateBranchSearch}
                                            items={branches || []}
                                            onChipChange={handleChipChange}
                                            selectedItems={selectedBranches || []}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container direction="row" wrap="nowrap">
                                    <Grid item xs={3}>
                                        {filterByAssignee}
                                    </Grid>
                                    <Grid item xs={2}>
                                        {filterByOverdueCheckbox}
                                    </Grid>
                                    <Grid item xs={3}>
                                        {filterByDueIn30Checkbox}
                                    </Grid>
                                    <Grid item xs={2}>
                                        {showArchivedCheckbox}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <DragDropContext onDragEnd={this.onDragEnd}>
                    <Grid
                        container
                        direction="row"
                        justify="space-around"
                        wrap="nowrap"
                        className={this.props.classes.actionItemBoard}
                    >
                        {actionStatuses &&
                            actionStatuses.map((actionStatus) =>
                                this.renderActionStatus(actionStatus)
                            )}
                    </Grid>
                    {actionItemDialog}
                </DragDropContext>
            </Grid>
        );
    }

    private onFilterByOverdueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.props.updateFilters!(this.props.filterByDueIn30!, event.target.checked, false, this.props.showArchived!);
    };

    private onFilterByDueIn30Change = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.props.updateFilters!(event.target.checked, this.props.filterByOverdue!, false, this.props.showArchived!);
    };

    private onShowArchived = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.props.updateFilters!(this.props.filterByDueIn30!, this.props.filterByOverdue!, false, event.target.checked);
    };

    private isOverdue(actionItem: IActionItem) {
        if (this.props.filterByOverdue) {
            return (
                actionItem.dueDate < new Date() &&
                actionItem.status.name !== ActionStatuses.Completed.name
            );
        }
        return true;
    }

    private isDueIn30Days(actionItem: IActionItem) {
        if (this.props.filterByDueIn30) {
            return (
                actionItem.dueDate < new Date(new Date().setDate(new Date().getDate() + 30)) &&
                actionItem.status.name !== ActionStatuses.Completed.name
            );
        }
        return true;
    }

    public openDialog = (actionItem: IActionItem) => () => {
        this.props.openDialogWithActionItem!(actionItem);
    };

    private renderActionItem(actionItem: IActionItem, index: number) {
        if (this.isOverdue(actionItem) && this.isDueIn30Days(actionItem)) {
            return (
                <Draggable key={actionItem.id} draggableId={actionItem.id} index={index}>
                    {(provided) => {
                        return (
                            <div
                                ref={provided.innerRef}
                                {...provided.dragHandleProps}
                                {...provided.draggableProps}
                            >
                                <ActionItem
                                    actionItem={actionItem}
                                    assessment={actionItem.assessment}
                                    editActionItem={this.openDialog(actionItem)}
                                />
                            </div>
                        );
                    }}
                </Draggable>
            );
        }
        return null;
    }

    private renderActionStatus(actionStatus: IActionStatus) {
        const backgroundColor = actionStatus.name
            ? ActionStatuses[actionStatus.name]
                ? ActionStatuses[actionStatus.name].background
                : ''
            : '';
        return (
            <Droppable droppableId={actionStatus.id} key={actionStatus.id}>
                {(provided) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={this.props.classes.columnContainerDiv}
                        style={{ backgroundColor }}
                    >
                        <Grid item className={this.props.classes.columnHeader}>
                            {actionStatus.name}
                        </Grid>
                        <Grid
                            container
                            direction="column"
                            className={this.props.classes.boardColumn}
                        >
                            <Grid item>
                                <Grid container direction="column" spacing={8}>
                                    {this.props.actionItems &&
                                        this.filterAndSortActionItemsByStatus(actionStatus).map(
                                            (actionItem: IActionItem, index: number) => (
                                                <Grid item key={actionItem.id}>
                                                    {actionItem.isActive &&
                                                        this.renderActionItem(actionItem, index)}
                                                </Grid>
                                            )
                                        )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                )}
            </Droppable>
        );
    }

    private filterAndSortActionItemsByStatus(actionStatus: IActionStatus): IActionItem[] {
        return this.props
            .actionItems!.filter((actionItem) => actionItem.statusId === actionStatus.id)
            .sort((a, b) => {
                return b.createdOn.getTime() - a.createdOn.getTime();
            });
    }

    public onDragEnd = (result: any) => {
        if (result.destination && this.props.actionItems) {
            const selectedStatus = this.props.actionStatuses!.find(
                (x) => x.id === result.destination.droppableId
            ) as IActionStatus;
            const selectedActionItem = this.props.actionItems.find(
                (x) => x.id === result.draggableId
            ) as IActionItem;

            if (selectedStatus) {
                selectedActionItem.statusId = selectedStatus.id;
                this.forceUpdate();
                this.props.saveActionItem!(selectedActionItem);
            }
        }
    };
}

const InjectedActionItems = inject<IStores, IActionItemsProps, Partial<IActionItemsProps>, any>(
    (stores: IStores) => ({
        actionItems: stores.actionItems.actionItems,
        actionStatuses: stores.actionStatuses.actionStatuses,
        allRegionNames: stores.actionItems.allRegionNames,
        assessments: stores.assessments.assessments,
        assignees: stores.actionItems.assignees,
        branches: stores.branches.branches,
        filter: stores.actionItems.filter,
        filterByDueIn30: stores.actionItems.filterByDueIn30,
        filterByOverdue: stores.actionItems.filterByOverdue,
        getActionStatuses: stores.actionStatuses.getAllActionStatuses,
        getAllActionItems: stores.actionItems.getAllActionItems,
        getAllAssessments: stores.assessments.getAllAssessments,
        getAllBranches: stores.branches.getAllBranches,
        isModalOpen: stores.actionItems.isModalOpen,
        onSearchChanged: stores.actionItems.onSearchChanged,
        openDialogWithActionItem: stores.actionItems.openDialogWithActionItem,
        saveActionItem: stores.actionItems.saveActionItem,
        selectedAssessments: stores.actionItems.selectedAssessments,
        selectedAssignees: stores.actionItems.selectedAssignees,
        selectedBranches: stores.actionItems.selectedBranches,
        selectedRegions: stores.actionItems.selectedRegions,
        setFilteredAssignees: stores.actionItems.setFilteredAssignees,
        setSelectedAssessments: stores.actionItems.setSelectedAssessments,
        setSelectedAssignees: stores.actionItems.setSelectedAssignees,
        setSelectedBranches: stores.actionItems.setSelectedBranches,
        setSelectedRegions: stores.actionItems.setSelectedRegions,
        showArchived: stores.actionItems.showArchived,
        updateFilters: stores.actionItems.updateFilters
    })
)(withStyles(styles)(ActionItems));

export default InjectedActionItems;



// WEBPACK FOOTER //
// ./src/actionItems/ActionItems.tsx